import { baseApi } from './base.api';
import { API_VERSION, PATH_BILLING_API } from '../constants/api-routes';
import {
  BILLING_SUBSCRIPTION_METHOD,
  BILLING_SUBSCRIPTION_UPDATE_MOD,
} from 'src/constants/billing';

// A saved payment method from the billing API.
export interface PaymentMethodItem {
  id?: string;
  default?: boolean;
  card_type?: string;
  // Card brand and last four digits, shown in the list.
  brand?: string;
  last4?: string;
  tax_id?: string;
  tax_id_value?: string;
  billing_details?: {
    name?: string;
    address?: {
      line1?: string;
      country?: string;
      state?: string;
      city?: string;
      postal_code?: string;
    };
  };
  [key: string]: unknown;
}

export interface PaymentMethodsResponse {
  data?: PaymentMethodItem[];
}

// A plan from the plans list.
export interface BillingPlan {
  id: number;
  name?: string;
  plan_type?: string;
  price: number;
  request_limit?: number;
  extra_request_price?: number;
  [key: string]: unknown;
}

// One charge in the account's payment history.
export interface Payment {
  id: string;
  description?: string;
  amount?: number;
  currency?: string;
  created?: string;
  status?: string;
}

// A page of the payment history; `starting_after` is the cursor for the next page.
export interface PaymentsPage {
  data?: Payment[];
  total?: number;
  has_more?: boolean;
  starting_after?: string;
}

export interface PaymentsArgs {
  starting_after?: string;
  start_date?: string;
  end_date?: string;
}

export interface InvoicePdf {
  receipt_url?: string;
}

export const billingService = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Callers pass {}, which is part of the cache key.
    getPaymentMethods: build.query<PaymentMethodsResponse, Record<string, never>>({
      query: () => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.PAYMENT_METHODS}`,
      }),
      providesTags: ['PaymentMethods'],
    }),
    getPayments: build.query<PaymentsPage, PaymentsArgs>({
      query: ({ starting_after = '', start_date = '', end_date = '' }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.PAYMENTS}`,
        params: {
          ...(starting_after !== '' && { starting_after }),
          ...(start_date !== '' && { start_date }),
          ...(end_date !== '' && { end_date }),
        },
      }),
      providesTags: ['Payments'],
    }),
    getAllPlans: build.query<BillingPlan[], void>({
      query: () => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.PLANS_ALL}`,
      }),
    }),
    addPaymentMethods: build.mutation({
      query: ({ payment_method_id, tax_id_value, tax_id_type, card_type, card_role }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.PAYMENT_METHODS}`,
        method: 'POST',
        body: {
          method: 'stripe',
          payment_method_id,
          tax_id_value,
          tax_id_type,
          card_type,
          card_role,
        },
      }),
      invalidatesTags: (result, error, { id }) => {
        if (error) {
          return [];
        }
        return [{ type: 'PaymentMethods', id }];
      },
    }),
    deletePaymentMethods: build.mutation({
      query: ({ payment_method_id }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.PAYMENT_METHODS}`,
        method: 'DELETE',
        body: {
          method: 'stripe',
          payment_method_id,
        },
      }),
      invalidatesTags: (result, error, { id }) => {
        if (error) {
          return [];
        }
        return [{ type: 'PaymentMethods', id }];
      },
    }),
    setDefaultPaymentMethod: build.mutation({
      query: ({ payment_method_id }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.PAYMENT_METHOD_DEFAULT}`,
        method: 'POST',
        body: {
          method: 'stripe',
          payment_method_id,
        },
      }),
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return [{ type: 'PaymentMethods' }];
      },
    }),
    getInvoicePdf: build.query<InvoicePdf, { payment_id: string }>({
      query: ({ payment_id }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.INVOICE_PDF}`,
        params: {
          payment_id,
        },
      }),
    }),
    activateSubscription: build.mutation({
      query: ({ subscription_items }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.CREATE_SUBSCRIPTION}`,
        method: 'POST',
        body: {
          method: 'stripe',
          subscription_items: subscription_items,
        },
      }),
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['ProjectPlans', 'Accounts'];
      },
    }),
    createSubscription: build.mutation({
      query: ({ subscription_item }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.CREATE_SUBSCRIPTION}`,
        method: 'POST',
        body: {
          method: BILLING_SUBSCRIPTION_METHOD.STRIPE,
          subscription_item: subscription_item,
        },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['Accounts'];
      },
    }),
    changeSubscription: build.mutation({
      query: ({
        subscription_item,
        subscription_update_mode = BILLING_SUBSCRIPTION_UPDATE_MOD.NEXT_PERIOD,
      }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.CHANGE_SUBSCRIPTION}`,
        method: 'PUT',
        body: {
          method: BILLING_SUBSCRIPTION_METHOD.STRIPE,
          subscription_update_mode: subscription_update_mode,
          subscription_item: subscription_item,
        },
      }),
      extraOptions: { maxRetries: 0 },
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['Accounts'];
      },
    }),
    cancelSubscription: build.mutation({
      query: ({ immediately = false }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.CANCEL_SUBSCRIPTION}`,
        method: 'POST',
        params: {
          immediately,
        },
      }),
      extraOptions: { maxRetries: 0 },
      async onQueryStarted({ immediately }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (immediately) {
            setTimeout(() => {
              dispatch(billingService.util.invalidateTags(['Accounts']));
            }, 1000);
          }
        } catch (error) {
          console.error('Запит не вдався:', error);
        }
      },
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['Accounts'];
      },
    }),
    createPaymentIntent: build.mutation({
      query: ({ amount, description }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.CREATE_PAYMENT_INTENT}`,
        method: 'POST',
        body: {
          amount,
          description,
        },
      }),
      invalidatesTags: (result, error) => {
        if (error) {
          return [];
        }
        return ['Accounts'];
      },
    }),
    checkoutSession: build.mutation({
      query: ({ plan_id }) => ({
        url: `${API_VERSION.V1}/${PATH_BILLING_API.CHECKOUT_SESSION}`,
        method: 'POST',
        body: {
          plan_id,
        },
      }),
      invalidatesTags: (result, error, { id }) => {
        if (error) {
          return [];
        }
        return ['Accounts'];
      },
    }),
  }),
});

export const {
  useGetAllPlansQuery,
  useGetPaymentsQuery,
  useGetPaymentMethodsQuery,
  useAddPaymentMethodsMutation,
  useSetDefaultPaymentMethodMutation,
  useActivateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useGetInvoicePdfQuery,
  useDeletePaymentMethodsMutation,
  useCreateSubscriptionMutation,
  useChangeSubscriptionMutation,
  useCreatePaymentIntentMutation,
  useCheckoutSessionMutation,
} = billingService;
